import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { savedApi } from '../services/api';
import { useAuth } from './AuthContext';
import { track } from '../utils/analytics';
import { setPref, typeFromCategory } from '../lib/pref';

const Ctx = createContext(null);
const LS_KEY = 'as_saved_ids';

const readLocal = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  } catch (e) {
    return [];
  }
};

const writeLocal = (ids) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ids));
  } catch (e) {
    /* ignore */
  }
};

// Saving an artist never requires login. Signed-out hosts keep their shortlist
// in localStorage; the first time they verify a phone number (OTP), the local
// list is merged into their account and localStorage is cleared.
export const SavedProvider = ({ children }) => {
  const { user, authChecked } = useAuth();
  const isCustomer = user?.role === 'customer';
  const [ids, setIds] = useState(() => readLocal());
  const mergedFor = useRef(null);

  useEffect(() => {
    if (!authChecked) return undefined;
    let active = true;
    (async () => {
      if (isCustomer) {
        if (mergedFor.current === user.user_id) return;
        mergedFor.current = user.user_id;
        const local = readLocal();
        try {
          if (local.length) {
            const res = await savedApi.merge(local);
            if (active) setIds(res.data.ids || []);
            writeLocal([]);
          } else {
            const res = await savedApi.get();
            if (active) setIds(res.data.ids || []);
          }
        } catch (e) {
          /* ignore — keep whatever we have */
        }
      } else {
        mergedFor.current = null;
        setIds(readLocal());
      }
    })();
    return () => { active = false; };
  }, [authChecked, isCustomer, user]);

  const isSaved = useCallback((id) => ids.includes(id), [ids]);

  const toggleSave = useCallback(async (artist) => {
    const id = artist?.artist_id || artist?.id;
    if (!id) return;
    const prev = ids;
    const has = prev.includes(id);
    const next = has ? prev.filter((x) => x !== id) : [...prev, id];
    setIds(next);
    toast[has ? 'message' : 'success'](has ? 'Removed from saved' : 'Saved to your shortlist');
    if (!has) {
      setPref({ category: typeFromCategory(artist?.category || artist?.artist_type) });
      track('save_artist', {
        artist_id: id,
        category: artist?.category || artist?.artist_type || null,
        city: artist?.city || artist?.base_city || null,
      });
    }
    if (isCustomer) {
      try {
        if (has) await savedApi.remove(id);
        else await savedApi.add(id);
      } catch (e) {
        setIds(prev); // revert on failure
      }
    } else {
      writeLocal(next);
    }
  }, [ids, isCustomer]);

  return (
    <Ctx.Provider value={{ ids, savedCount: ids.length, isSaved, toggleSave }}>
      {children}
    </Ctx.Provider>
  );
};

export const useSaved = () => {
  const ctx = useContext(Ctx);
  if (!ctx) return { ids: [], savedCount: 0, isSaved: () => false, toggleSave: () => {} };
  return ctx;
};

export default SavedProvider;
